import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  LayoutDashboard,
  Radio,
  Target,
  Link2,
  Zap,
  Send,
  AlertTriangle,
  Building2,
  Webhook,
  Mail,
  KeyRound,
  Bell,
  Bookmark,
  Activity,
  FileJson,
  Code2,
  Satellite,
  ExternalLink,
  Unplug,
  ScrollText,
  ShieldCheck,
  FolderSync,
  Settings,
} from "lucide-react"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"

const pages = [
  { label: "Dashboard", to: "/", icon: LayoutDashboard, group: "Navigation" },
  { label: "Sources", to: "/sources", icon: Radio, group: "Inbound" },
  { label: "Destinations", to: "/destinations", icon: Target, group: "Inbound" },
  { label: "Subscriptions", to: "/subscriptions", icon: Link2, group: "Inbound" },
  { label: "Events", to: "/events", icon: Zap, group: "Inbound" },
  { label: "Deliveries", to: "/deliveries", icon: Send, group: "Inbound" },
  { label: "Bookmarks", to: "/bookmarks", icon: Bookmark, group: "Inbound" },
  { label: "Dead Letter Queue", to: "/dlq", icon: AlertTriangle, group: "Inbound" },
  { label: "Applications", to: "/applications", icon: Building2, group: "Outbound" },
  { label: "Endpoints", to: "/endpoints", icon: Webhook, group: "Outbound" },
  { label: "Messages", to: "/messages", icon: Mail, group: "Outbound" },
  { label: "Outbound Deliveries", to: "/outbound-deliveries", icon: Send, group: "Outbound" },
  { label: "Streams", to: "/streams", icon: Activity, group: "Outbound" },
  { label: "Outbound DLQ", to: "/outbound-dlq", icon: AlertTriangle, group: "Outbound" },
  { label: "Event Types", to: "/event-types", icon: FileJson, group: "Settings" },
  { label: "Transformations", to: "/transformations", icon: Code2, group: "Settings" },
  { label: "API Keys", to: "/api-keys", icon: KeyRound, group: "Settings" },
  { label: "Alert Rules", to: "/alert-rules", icon: Bell, group: "Settings" },
  { label: "Meta Events", to: "/meta-events", icon: Satellite, group: "Settings" },
  { label: "Portal Links", to: "/portal-links", icon: ExternalLink, group: "Settings" },
  { label: "Tunnels", to: "/tunnels", icon: Unplug, group: "Settings" },
  { label: "Audit Logs", to: "/audit-logs", icon: ScrollText, group: "Settings" },
  { label: "Verification Test", to: "/verification-test", icon: ShieldCheck, group: "Settings" },
  { label: "Config Export", to: "/config", icon: FolderSync, group: "Settings" },
  { label: "Settings", to: "/settings", icon: Settings, group: "Settings" },
]

const groups = ["Navigation", "Inbound", "Outbound", "Settings"]

export function SearchPalette() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [])

  const go = useCallback(
    (to: string) => {
      setOpen(false)
      navigate(to)
    },
    [navigate],
  )

  return (
    <CommandDialog open={open} onOpenChange={setOpen} title="Go to page" description="Navigate to a page">
      <CommandInput placeholder="Search pages..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {groups.map((group) => {
          const items = pages.filter((p) => p.group === group)
          return (
            <CommandGroup key={group} heading={group}>
              {items.map((item) => (
                <CommandItem key={item.to} onSelect={() => go(item.to)}>
                  <item.icon className="h-4 w-4 mr-2 text-muted-foreground" />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          )
        })}
      </CommandList>
    </CommandDialog>
  )
}
